import React, { useState, useEffect } from 'react';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { DownloadSimple, Upload, FileText, ListChecks, Check, ArrowLeft, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

/* ───────────────── Normalize stored field types ───────────────── */
const normalizeType = (type) => {
  const map = {
    'heading': 'section', 'text_input': 'text_row', 'long_text': 'long_text_row',
    'date_input': 'date_row', 'number_input': 'text_row', 'email_input': 'text_row',
    'phone_input': 'text_row', 'yes_no': 'yes_no_row', 'checklist': 'checkbox_row',
    'dropdown': 'dropdown_row', 'paragraph': 'note',
  };
  return map[type] || type;
};

/* ───────────────── Document-style form table row (client fills in) ───────────────── */
function FormTableRow({ field, responses, onChange, disabled }) {
  const type = normalizeType(field.type);
  const cellBorder = 'border border-gray-400';
  const fid = field.id;

  // SECTION HEADING
  if (type === 'section') {
    return (
      <tr>
        <td colSpan={2} className={`${cellBorder} bg-gray-200 px-4 py-3 font-bold text-base text-gray-800 uppercase tracking-wide`}>
          {field.content || field.label || ''}
        </td>
      </tr>
    );
  }

  // NOTE / INSTRUCTIONS
  if (type === 'note') {
    return (
      <tr>
        <td colSpan={2} className={`${cellBorder} px-4 py-3 text-base text-gray-600 italic whitespace-pre-wrap`}>
          {field.content || ''}
        </td>
      </tr>
    );
  }

  // CHECKBOX ROW (old checklist items or new single checkbox)
  if (type === 'checkbox_row') {
    const items = field.items && field.items.length > 0 ? field.items : [field.label || ''];
    const checked = responses[fid] && typeof responses[fid] === 'object' ? responses[fid] : {};

    return (
      <>
        {field.label && field.items && field.items.length > 0 && (
          <tr>
            <td colSpan={2} className={`${cellBorder} bg-gray-100 px-4 py-2 font-semibold text-base text-gray-700`}>
              {field.label}
            </td>
          </tr>
        )}
        {items.map((item, idx) => (
          <tr key={`${fid}-${idx}`}>
            <td className={`${cellBorder} bg-gray-50 px-4 py-3 text-base text-gray-800 w-[55%]`}>
              {item}
            </td>
            <td className={`${cellBorder} px-4 py-3 text-center`}>
              <input
                type="checkbox"
                checked={!!checked[idx]}
                onChange={(e) => onChange(fid, { ...checked, [idx]: e.target.checked })}
                disabled={disabled}
                className="w-5 h-5 rounded cursor-pointer"
                data-testid={`checklist-${fid}-${idx}`}
              />
            </td>
          </tr>
        ))}
      </>
    );
  }

  // YES / NO / ATTACHED
  if (type === 'yes_no_row') {
    const value = responses[fid] || '';
    return (
      <tr>
        <td className={`${cellBorder} bg-gray-50 px-4 py-3 text-base text-gray-800 w-[55%]`}>
          {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </td>
        <td className={`${cellBorder} px-4 py-3`}>
          <div className="flex gap-5">
            <label className="flex items-center gap-2 cursor-pointer text-base">
              <input
                type="radio"
                name={`yesno-${fid}`}
                checked={value === 'Yes'}
                onChange={() => !disabled && onChange(fid, 'Yes')}
                disabled={disabled}
                className="w-5 h-5"
                data-testid={`yesno-yes-${fid}`}
              />
              Yes
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-base">
              <input
                type="radio"
                name={`yesno-${fid}`}
                checked={value === 'No'}
                onChange={() => !disabled && onChange(fid, 'No')}
                disabled={disabled}
                className="w-5 h-5"
                data-testid={`yesno-no-${fid}`}
              />
              No
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-base">
              <input
                type="radio"
                name={`yesno-${fid}`}
                checked={value === 'Attached'}
                onChange={() => !disabled && onChange(fid, 'Attached')}
                disabled={disabled}
                className="w-5 h-5"
                data-testid={`yesno-attached-${fid}`}
              />
              Attached
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-base">
              <input
                type="radio"
                name={`yesno-${fid}`}
                checked={value === 'N/A'}
                onChange={() => !disabled && onChange(fid, 'N/A')}
                disabled={disabled}
                className="w-5 h-5"
                data-testid={`yesno-na-${fid}`}
              />
              N/A
            </label>
          </div>
        </td>
      </tr>
    );
  }

  // DROPDOWN
  if (type === 'dropdown_row') {
    const value = responses[fid] || '';
    const opts = Array.isArray(field.options) ? field.options : [];
    return (
      <tr>
        <td className={`${cellBorder} bg-gray-50 px-4 py-3 text-base text-gray-800 w-[55%]`}>
          {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </td>
        <td className={`${cellBorder} px-3 py-2`}>
          <Select value={value} onValueChange={(v) => onChange(fid, v)} disabled={disabled}>
            <SelectTrigger className="h-10 text-base" data-testid={`form-field-${fid}`}>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
      </tr>
    );
  }

  // LONG TEXT ROW
  if (type === 'long_text_row') {
    const value = responses[fid] || '';
    return (
      <tr>
        <td className={`${cellBorder} bg-gray-50 px-4 py-3 text-base text-gray-800 w-[55%] align-top`}>
          {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
        </td>
        <td className={`${cellBorder} px-3 py-2`}>
          <textarea
            value={value}
            onChange={(e) => onChange(fid, e.target.value)}
            disabled={disabled}
            placeholder="Write here..."
            className="w-full min-h-[80px] rounded border border-gray-300 bg-white px-3 py-2 text-base resize-y focus:outline-none focus:border-gray-500"
            data-testid={`form-field-${fid}`}
          />
        </td>
      </tr>
    );
  }

  // DEFAULT: TEXT / DATE / NUMBER / EMAIL / PHONE
  const value = responses[fid] || '';
  const inputType = field.type === 'date_input' || type === 'date_row' ? 'date' : 'text';

  return (
    <tr>
      <td className={`${cellBorder} bg-gray-50 px-4 py-3 text-base text-gray-800 w-[55%]`}>
        {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
      </td>
      <td className={`${cellBorder} px-3 py-2`}>
        <Input
          type={inputType}
          value={value}
          onChange={(e) => onChange(fid, e.target.value)}
          disabled={disabled}
          placeholder={inputType === 'date' ? '' : 'Write here...'}
          className="h-10 text-base border-gray-300"
          data-testid={`form-field-${fid}`}
        />
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function ClientFormsAndDocs() {
  const [sharedDocs, setSharedDocs] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingResponse, setUploadingResponse] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [docsRes, formsRes] = await Promise.all([
        clientApi.get('/shared-documents'),
        clientApi.get('/forms'),
      ]);
      setSharedDocs(docsRes.data);
      setForms(formsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const baseUrl = clientApi.defaults.baseURL.replace('/client-portal', '');
      const response = await clientApi.get(`${baseUrl}/files/${doc.file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.document_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleUploadResponse = async (docId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingResponse(docId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await clientApi.post(
        `/shared-documents/${docId}/respond`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setSharedDocs(sharedDocs.map(d => d.id === docId ? data : d));
      toast.success('Document uploaded successfully!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingResponse(null);
      e.target.value = '';
    }
  };

  const handleOpenForm = (form) => {
    setActiveForm(form);
    setFormResponses(form.responses || {});
  };

  const handleFormFieldChange = (fieldId, value) => {
    setFormResponses({ ...formResponses, [fieldId]: value });
  };

  const handleSubmitForm = async () => {
    if (!activeForm) return;
    const missingRequired = (activeForm.fields || [])
      .filter(f => f.required && !['heading', 'paragraph', 'section', 'note'].includes(f.type) && !['heading', 'paragraph'].includes(f.type))
      .filter(f => {
        const val = formResponses[f.id];
        return !val && val !== false && val !== 0;
      })
      .map(f => f.label);

    if (missingRequired.length > 0) {
      toast.error(`Please fill in: ${missingRequired.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await clientApi.post(`/forms/${activeForm.id}/submit`, { responses: formResponses });
      setForms(forms.map(f => f.id === activeForm.id ? data : f));
      setActiveForm(null);
      setFormResponses({});
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!activeForm) return;
    setDownloadingPdf(true);
    try {
      const response = await clientApi.post(
        `/forms/${activeForm.id}/download-pdf`,
        { responses: formResponses },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeForm.form_name || 'form'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* ═══════ FORM FILL VIEW ═══════ */
  if (activeForm) {
    const isSubmitted = activeForm.status === 'submitted';
    return (
      <div className="space-y-6" data-testid="form-fill-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setActiveForm(null); setFormResponses({}); }} data-testid="form-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            {isSubmitted && <Badge className="bg-green-50 text-green-600">Submitted</Badge>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
          >
            <FilePdf className="w-4 h-4 mr-1" />
            {downloadingPdf ? 'Downloading...' : 'Download PDF'}
          </Button>
        </div>

        {/* Document-style form with table */}
        <div className="bg-gray-100 rounded-lg p-4 sm:p-6">
          <div className="bg-white border-2 border-gray-400 shadow max-w-[800px] mx-auto" data-testid="form-document">
            {/* Document Header */}
            <div className="border-b-2 border-gray-400 px-6 py-5 text-center">
              <h2 className="font-bold text-xl text-gray-900">{activeForm.form_name}</h2>
              {activeForm.form_description && (
                <p className="text-sm text-gray-500 mt-1">{activeForm.form_description}</p>
              )}
              <p className="text-xs text-gray-400 mt-3">Applegrow Financial Advisors</p>
            </div>

            {/* Form Fields as Table with rows and columns */}
            <table className="w-full border-collapse">
              <tbody>
                {(activeForm.fields || []).map((field) => (
                  <FormTableRow
                    key={field.id}
                    field={field}
                    responses={formResponses}
                    onChange={handleFormFieldChange}
                    disabled={isSubmitted}
                  />
                ))}
              </tbody>
            </table>

            {/* Submit Button */}
            {!isSubmitted && (
              <div className="border-t-2 border-gray-400 px-6 py-4 flex justify-end">
                <Button onClick={handleSubmitForm} disabled={submitting} size="lg" data-testid="submit-form-btn">
                  <Check className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting...' : 'Submit Form'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══════ MAIN LIST VIEW ═══════ */
  return (
    <div className="space-y-6" data-testid="client-forms-docs-page">
      <div>
        <h1 className="heading-2 mb-1">Forms & Documents</h1>
        <p className="text-muted-foreground">Documents from your accountant and online forms to complete</p>
      </div>

      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList>
          <TabsTrigger value="documents" data-testid="tab-shared-docs">
            <FileText className="w-4 h-4 mr-2" />
            Documents ({sharedDocs.length})
          </TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-online-forms">
            <ListChecks className="w-4 h-4 mr-2" />
            Online Forms ({forms.length})
          </TabsTrigger>
        </TabsList>

        {/* ── Shared Documents Tab ── */}
        <TabsContent value="documents">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Documents from Accountant</CardTitle>
              <CardDescription>Download, fill in, and upload the completed document</CardDescription>
            </CardHeader>
            <CardContent>
              {sharedDocs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No documents shared yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sharedDocs.map((doc) => (
                    <div key={doc.id} className="p-4 border rounded-md space-y-3" data-testid={`shared-doc-${doc.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-base">{doc.document_name}</p>
                          <p className="text-xs text-muted-foreground">
                            From {doc.admin_name || 'your accountant'} | {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={
                          doc.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                        }>
                          {doc.status === 'completed' ? 'Completed' : 'Action Required'}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleDownloadDoc(doc)} data-testid={`download-doc-${doc.id}`}>
                          <DownloadSimple className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        {doc.status !== 'completed' ? (
                          <>
                            <input
                              type="file"
                              id={`respond-${doc.id}`}
                              onChange={(e) => handleUploadResponse(doc.id, e)}
                              className="hidden"
                              data-testid={`respond-input-${doc.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => document.getElementById(`respond-${doc.id}`).click()}
                              disabled={uploadingResponse === doc.id}
                              data-testid={`upload-response-${doc.id}`}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              {uploadingResponse === doc.id ? 'Uploading...' : 'Upload Completed Document'}
                            </Button>
                          </>
                        ) : (
                          <div className="flex items-center text-sm text-green-600 gap-1">
                            <Check className="w-4 h-4" />
                            Completed — {doc.response_filename}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Online Forms Tab ── */}
        <TabsContent value="forms">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Online Forms</CardTitle>
              <CardDescription>Fill in these forms online or download as PDF to complete offline</CardDescription>
            </CardHeader>
            <CardContent>
              {forms.length === 0 ? (
                <div className="text-center py-12">
                  <ListChecks className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No forms assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {forms.map((form) => (
                    <div key={form.id} className="flex items-center justify-between p-4 border rounded-md" data-testid={`form-card-${form.id}`}>
                      <div>
                        <p className="font-medium text-base">{form.form_name}</p>
                        {form.form_description && (
                          <p className="text-sm text-muted-foreground">{form.form_description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {(form.fields || []).length} fields | Assigned {new Date(form.assigned_at).toLocaleDateString()}
                          {form.submitted_at && ` | Submitted ${new Date(form.submitted_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          form.status === 'submitted' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                        }>
                          {form.status === 'submitted' ? 'Submitted' : 'Pending'}
                        </Badge>
                        <Button
                          variant={form.status === 'submitted' ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => handleOpenForm(form)}
                          data-testid={`open-form-${form.id}`}
                        >
                          {form.status === 'submitted' ? 'View' : 'Fill In'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
